import React from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';

import { RCText, RCTextWithIcon, useRCPopover } from '@reverbdotcom/cadence/components';
import { ReverbLogoCircleIcon } from '@reverbdotcom/cadence/icons/react';
import { PriceDisplay } from '@reverbdotcom/commons';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import {
  core_apimessages_Money as Money,
  core_apimessages_MyShopOnboardingResponse_PaymentMethod,
} from '@reverbdotcom/commons/src/gql/graphql';

import { Paths } from '../../../components/shared/url_helpers';

export const WALLET_HC_ID = '43454547005075';

interface Props {
  walletBalance: Money,
  reverbCreditsBalances: Money[],
  payoutBalances: Money[],
  paymentMethod: string;
}

function WalletBalance({ balance }) {
  return (
    <div className="d-flex fx-align-center fx-justify-between mb-2">
      <div className="d-flex fx-align-center text-color-vu-orange-600">
        <RCTextWithIcon icon={<ReverbLogoCircleIcon />} placement="left">
          <RCText.Utility
            size="350"
            color="secondary"
            weight="semibold"
            htmlTag="span"
          >
            {I18n.t('discovery.reverbHeader.userMenu.wallet')}
          </RCText.Utility>
        </RCTextWithIcon>
      </div>
      <div className={classNames(
        'reverb-header__menu__combined_credits__balance',
        'reverb-header__menu__combined_credits__wallet-balance',
        { 'reverb-header__menu__combined_credits__balance-zero': balance.amountCents == 0 },
      )}>
        <PriceDisplay display={balance.display} />
      </div>
    </div>
  );
}

export default function ReverbHeaderCreditCallouts({ walletBalance, reverbCreditsBalances, payoutBalances, paymentMethod }: Props) {
  // Desktop renders this inside the user menu popover, which a click on its own content
  // won't dismiss. A no-op anywhere else. Above the early return, so the hook order holds.
  const { close } = useRCPopover();

  if (!walletBalance && !reverbCreditsBalances?.length && !payoutBalances?.length) { return null; }

  const myEarningsPayoutsPath = Paths.myEarningsPayouts.expand({});
  const creditsOtherThanWallet = reverbCreditsBalances
    .filter((balance) => balance.currency != walletBalance?.currency);

  return (
    <div className="bdb-1 bd-primary pt-2 plr-2">
      {walletBalance && (
        <>
          {paymentMethod == core_apimessages_MyShopOnboardingResponse_PaymentMethod.REVERB_PAYMENTS && (
            <CoreLink
              to={myEarningsPayoutsPath}
              onClick={close}
            >
              <WalletBalance
                balance={walletBalance}
              />
            </CoreLink>
          )}

          {paymentMethod != core_apimessages_MyShopOnboardingResponse_PaymentMethod.REVERB_PAYMENTS && (
            <WalletBalance
              balance={walletBalance}
            />
          )}
        </>
      )}
      {!!creditsOtherThanWallet && (
        creditsOtherThanWallet.map(({ display, currency }, index) => (
          <div className="d-flex fx-align-center fx-justify-between mb-2" key={`reverb_credits_${currency}`}>
            {index == 0 ? (
              <RCText.Utility size="350" color="secondary" weight="semibold">
                {I18n.t('discovery.reverbHeader.userMenu.credits')}
              </RCText.Utility>
            ) : <div/>}
            <div className="reverb-header__menu__combined_credits__balance reverb-header__menu__combined_credits__credit-balance">
              <PriceDisplay display={display}/>
            </div>
          </div>
        ))
      )}
      {!!payoutBalances && (
        payoutBalances.map(({ display, currency }, index) => (
          <div className="d-flex fx-align-center fx-justify-between mb-2" key={`payout_balances_${currency}`}>
            {index == 0 ? (
              <RCText.Utility size="350" color="secondary" weight="semibold">
                {I18n.t('discovery.reverbHeader.userMenu.payout')}
              </RCText.Utility>
            ) : <div/>}
            <div className="reverb-header__menu__combined_credits__balance reverb-header__menu__combined_credits__payout-balance">
              <PriceDisplay display={display} />
            </div>
          </div>
        ))
      )}
    </div>
  );
}
